///
/// EarthBrowser
///
/// Created by Matt Giger on 7/16/2024
/// Copyright (c) 2024 Geodata Labs
///

import { cn } from '@/components/utils/utils'

// Starfield: deterministic positions (viewBox 0 0 1000 600), split into three
// groups that twinkle on staggered cycles
const STARS: Array<[number, number, number]> = [
	[36, 60, 1.1], [112, 210, 0.8], [180, 90, 1.3], [235, 320, 0.7], [290, 150, 1.0],
	[340, 45, 0.8], [395, 260, 1.2], [450, 110, 0.7], [505, 330, 0.9], [548, 70, 1.2],
	[602, 190, 0.8], [660, 40, 1.0], [712, 280, 1.3], [768, 130, 0.7], [820, 220, 1.0],
	[875, 65, 1.2], [930, 300, 0.8], [968, 160, 1.0], [70, 340, 0.9], [150, 400, 0.7],
	[260, 30, 1.0], [420, 380, 0.8], [575, 350, 1.1], [700, 370, 0.7], [850, 390, 1.0],
	[95, 120, 0.7], [310, 240, 0.9], [490, 200, 1.1], [640, 120, 0.7], [795, 40, 0.9],
	[905, 130, 0.7], [55, 250, 1.2], [215, 170, 0.8], [370, 330, 1.0], [530, 30, 0.8],
	[755, 320, 0.9],
]

export function Loader({ className }: { className?: string }) {
	return (
		<div
			role="status"
			aria-label="Loading EarthBrowser"
			className={cn('eb-loader fixed inset-0 overflow-hidden flex flex-col items-center justify-center', className)}
		>
			<style>{loaderCss}</style>

			{/* Starfield + satellite pass (slice keeps circles round at any aspect) */}
			<svg
				className="absolute inset-0 w-full h-full"
				viewBox="0 0 1000 600"
				preserveAspectRatio="xMidYMid slice"
				aria-hidden="true"
			>
				<g className="eb-stars eb-stars-a" fill="#e2e8f0">
					{STARS.filter((_, i) => i % 3 === 0).map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} />)}
				</g>
				<g className="eb-stars eb-stars-b" fill="#bae6fd">
					{STARS.filter((_, i) => i % 3 === 1).map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} />)}
				</g>
				<g className="eb-stars eb-stars-c" fill="#e0f2fe">
					{STARS.filter((_, i) => i % 3 === 2).map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} />)}
				</g>

				{/* Faint orbit track */}
				<path
					className="eb-orbit-track"
					d="M -50 210 C 250 60, 750 60, 1050 210"
					fill="none"
					stroke="#67e8f9"
					strokeWidth="1"
					strokeDasharray="1 9"
					opacity="0.25"
				/>

				{/* Satellite pass along the track */}
				<g className="eb-sat">
					<circle r="9" fill="url(#eb-sat-halo)">
						<animateMotion dur="9s" repeatCount="indefinite" path="M -50 210 C 250 60, 750 60, 1050 210" />
					</circle>
					<circle r="2.2" fill="#f8fafc">
						<animateMotion dur="9s" repeatCount="indefinite" path="M -50 210 C 250 60, 750 60, 1050 210" />
					</circle>
					<animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="9s" repeatCount="indefinite" />
				</g>

				<defs>
					<radialGradient id="eb-sat-halo">
						<stop offset="0%" stopColor="#67e8f9" stopOpacity="0.7" />
						<stop offset="100%" stopColor="#67e8f9" stopOpacity="0" />
					</radialGradient>
				</defs>
			</svg>

			{/* Planet limb rising from the bottom, with atmosphere rim and sweeping dawn glint */}
			<div className="eb-planet" aria-hidden="true">
				<div className="eb-glint-arm">
					<div className="eb-glint" />
				</div>
			</div>

			<div className="relative z-10 flex flex-col items-center -translate-y-8">
				<h1 className="eb-wordmark text-3xl font-semibold tracking-wide bg-gradient-to-r from-blue-500 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
					EarthBrowser
				</h1>

				<div className="eb-progress mt-5" />
			</div>
		</div>
	)
}

const loaderCss = `
.eb-loader {
	background:
		radial-gradient(120% 90% at 50% 110%, rgba(8, 47, 73, 0.55) 0%, rgba(2, 6, 23, 0) 55%),
		linear-gradient(to bottom, #020617 0%, #04091c 60%, #071223 100%);
}

/* --- Planet limb --- */
.eb-planet {
	position: absolute;
	top: 72%;
	left: 50%;
	width: max(240vw, 140vh);
	aspect-ratio: 1;
	transform: translateX(-50%);
	border-radius: 50%;
	background: radial-gradient(circle at 50% 0%, #0d2440 0%, #081627 12%, #040b16 30%, #020610 55%);
	border-top: 1px solid rgba(103, 232, 249, 0.55);
	box-shadow:
		0 -4px 24px rgba(34, 211, 238, 0.35),
		0 -24px 80px rgba(37, 99, 235, 0.28),
		0 -80px 220px rgba(30, 64, 175, 0.22);
}

/* Sun glint sweeping along the limb (arm rotates about planet center) */
.eb-glint-arm {
	position: absolute;
	inset: 0;
	animation: eb-glint-sweep 18s ease-in-out infinite alternate;
	will-change: transform;
}
.eb-glint {
	position: absolute;
	top: 0;
	left: 50%;
	width: min(44vw, 480px);
	height: min(12vh, 110px);
	transform: translate(-50%, -50%);
	border-radius: 50%;
	background: radial-gradient(closest-side, rgba(254, 243, 199, 0.9), rgba(103, 232, 249, 0.35) 55%, transparent);
	filter: blur(14px);
}
@keyframes eb-glint-sweep {
	from { transform: rotate(-7deg); }
	to { transform: rotate(7deg); }
}

/* --- Stars --- */
.eb-stars { animation: eb-twinkle 4.5s ease-in-out infinite; }
.eb-stars-b { animation-delay: -1.5s; }
.eb-stars-c { animation-delay: -3s; }
@keyframes eb-twinkle {
	0%, 100% { opacity: 0.9; }
	50% { opacity: 0.35; }
}

/* --- Wordmark and progress shimmer --- */
.eb-wordmark { animation: eb-rise 1.2s ease-out both; }
@keyframes eb-rise {
	from { opacity: 0; transform: translateY(10px); letter-spacing: 0.3em; }
	to { opacity: 1; transform: translateY(0); letter-spacing: 0.05em; }
}

.eb-progress {
	width: 9rem;
	height: 3px;
	border-radius: 9999px;
	overflow: hidden;
	background: rgba(148, 163, 184, 0.15);
	position: relative;
}
.eb-progress::after {
	content: '';
	position: absolute;
	inset: 0;
	border-radius: 9999px;
	background: linear-gradient(to right, transparent, #22d3ee 40%, #2dd4bf 60%, transparent);
	transform: translateX(-100%);
	animation: eb-shimmer 1.8s ease-in-out infinite;
}
@keyframes eb-shimmer {
	to { transform: translateX(100%); }
}

/* --- Reduced motion: static scene, satellite hidden --- */
@media (prefers-reduced-motion: reduce) {
	.eb-loader *, .eb-loader *::after {
		animation: none !important;
	}
	.eb-sat { display: none; }
	.eb-wordmark { opacity: 1; }
}
`
